import { useCallback, useMemo, useReducer } from "react";
import { EMPTY_PATIENT } from "../constants/registrationConstants";

const clone = (obj) => JSON.parse(JSON.stringify(obj));

function patientReducer(state, action) {
  switch (action.type) {
    case "SET_FN": {
      const next = action.updater(state);
      return next === state ? state : next;
    }
    case "SET":
      return { ...state, ...action.payload };
    case "RESET":
      return clone(EMPTY_PATIENT);
    case "UPDATE_FIELD":
      return { ...state, [action.field]: action.value };
    case "UPDATE_ENCOUNTER": {
      return {
        ...state,
        encounter: { ...state.encounter, [action.field]: action.value },
      };
    }
    case "UPDATE_SOURCE": {
      return {
        ...state,
        source: { ...state.source, [action.field]: action.value },
      };
    }
    case "UPDATE_LIST_ITEM": {
      const list = [...state[action.listKey]];
      list[action.index] = { ...list[action.index], [action.field]: action.value };
      return { ...state, [action.listKey]: list };
    }
    case "ADD_LIST_ITEM":
      return { ...state, [action.listKey]: [...state[action.listKey], action.item] };
    case "REMOVE_LIST_ITEM": {
      return {
        ...state,
        [action.listKey]: state[action.listKey].filter((_, i) => i !== action.index),
      };
    }
    case "REPLACE_LIST":
      return { ...state, [action.listKey]: action.items };
    case "MERGE":
      return { ...state, ...action.payload, encounter: { ...state.encounter, ...(action.payload.encounter || {}) } };
    default:
      return state;
  }
}

export function usePatientForm(initial = EMPTY_PATIENT) {
  const [patient, dispatch] = useReducer(patientReducer, initial, () => clone(initial));

  const setPatient = useCallback((updater) => {
    if (typeof updater === "function") {
      dispatch({ type: "SET_FN", updater });
    } else {
      dispatch({ type: "SET", payload: updater });
    }
  }, []);

  const resetPatient = useCallback(() => dispatch({ type: "RESET" }), []);

  const updateField = useCallback((field, value) => {
    dispatch({ type: "UPDATE_FIELD", field, value });
  }, []);

  const updateEncounter = useCallback((field, value) => {
    dispatch({ type: "UPDATE_ENCOUNTER", field, value });
  }, []);

  const updateSource = useCallback((field, value) => {
    dispatch({ type: "UPDATE_SOURCE", field, value });
  }, []);

  const updateListItem = useCallback((listKey, index, field, value) => {
    dispatch({ type: "UPDATE_LIST_ITEM", listKey, index, field, value });
  }, []);

  const addListItem = useCallback((listKey, item) => {
    dispatch({ type: "ADD_LIST_ITEM", listKey, item });
  }, []);

  const removeListItem = useCallback((listKey, index) => {
    dispatch({ type: "REMOVE_LIST_ITEM", listKey, index });
  }, []);

  const mergePatient = useCallback((payload) => {
    dispatch({ type: "MERGE", payload });
  }, []);

  const actions = useMemo(() => ({
    setPatient,
    resetPatient,
    updateField,
    updateEncounter,
    updateSource,
    updateListItem,
    addListItem,
    removeListItem,
    mergePatient,
  }), [
    setPatient, resetPatient, updateField, updateEncounter, updateSource,
    updateListItem, addListItem, removeListItem, mergePatient,
  ]);

  return { patient, dispatch, ...actions };
}
